import { useQuery } from '@apollo/client';
import { useMelwaysInfo } from '@seek/melways-react';
import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import { Alert, Stack, Strong, Text } from 'braid-design-system';
import React, { useEffect } from 'react';

import { browserLogError } from 'src/modules/error-logger/browserLog';
import { getPreferenceQL } from 'src/modules/graphql/notifications/preference.graphql';
import { useTypedSelector } from 'src/reducers';
import type {
  GetPreferenceQuery,
  NotificationPreference,
} from 'src/types/graphql';

import Category from '../category';
import {
  CategoryName,
  type CustomCategory,
  generateProfileDescriptionVocabKey,
} from '../custom-category';
import { useUserProfile } from '../hooks/useUserProfile';
import { PageTitles } from '../pageTitle';

import translations from './.vocab';

const Preferences = () => {
  const { language } = useMelwaysInfo();
  const locale = useTypedSelector(
    (state) => state.page.pageAnalytics.siteCountry,
  ).toUpperCase();

  const brand =
    PageTitles[useTypedSelector((state) => state.page.pageAnalytics.brand)] ??
    PageTitles.seek;

  const { authenticationStatus } = useUserProfile();
  const isAuthenticated = authenticationStatus === 'authenticated';

  const { data, error } = useQuery<GetPreferenceQuery>(getPreferenceQL, {
    variables: {
      locale: `${language}-${locale}`,
    },
  });

  const isQueryError = isAuthenticated && error;

  const { t } = useTranslations(translations);

  // This was moved from savedSearchUpdates and hearFromEmployers in custom-category to here because we need t() and useTranslations.
  // Tried adding it into custom-category and it introduce random error of Too Many Hook call.
  const customCategories: CustomCategory[] = [
    {
      product: {
        label: t('category-managed-saved-search-product'),
        descriptionJsx: <>{t('category-managed-saved-search-description')}</>,
      },
      action: {
        label: t('category-managed-saved-search-link-label'),
        link: 'savedSearchesPath',
      },
      testId: 'saved-search',
    },
    {
      product: {
        label: t('category-profile-product'),
        descriptionJsx: (
          <>
            {t(
              generateProfileDescriptionVocabKey(
                data?.viewer?.profileVisibility?.level,
              ),
              {
                Strong: (children) => (
                  <Strong id="profile-level">{children}</Strong>
                ),
                brand,
              },
            )}
          </>
        ),
      },
      action: {
        label: t('category-profile-link-label'),
        link: 'profileVisibilityPath',
      },
      testId: 'hear-employers',
    },
  ];

  useEffect(() => {
    if (isQueryError) {
      metrics.count('preferences-error-page-rendered');
    } else {
      metrics.count('preferences-page-rendered');
    }
  }, [error, isAuthenticated, isQueryError]);

  if (isQueryError) {
    browserLogError(error);
    return (
      <Alert tone="critical">
        <Text>{t('preferences-alert-message')}</Text>
      </Alert>
    );
  }

  return (
    <Stack space="xlarge">
      <Alert tone="info">
        <Text>
          {t('preferences-info-alert-message', {
            brand,
          })}
        </Text>
      </Alert>
      <Stack space="xxlarge">
        {data?.viewer?.notificationPreferenceGroups?.map((x, i) => {
          let prefs: Array<
            CustomCategory | Omit<NotificationPreference, 'category'>
          > = x.notificationPreferences;
          if (x.category?.code === CategoryName.OPPORTUNITIES) {
            prefs = [...x.notificationPreferences, ...customCategories];
          }
          return <Category key={i} category={x.category} preferences={prefs} />;
        })}
      </Stack>
    </Stack>
  );
};

export default Preferences;
